import { useState } from "react";
import { VendorAvatar } from "@semo/shared-ui";

import { useMyCompanyProfile } from "../hooks/useMyCompanyProfile";
import { MyCompanyProfileSheet } from "./MyCompanyProfileSheet";

interface MyProfileChipProps {
  companyName: string;
}

/**
 * 판매자 센터 헤더의 «내 프로필» 칩.
 *
 * 예전에는 회사명 텍스트뿐이었고 좁은 화면에서는 아예 사라졌다.
 * 로고(없으면 첫 글자) 아바타는 모든 화면 폭에서 남기고, 클릭하면
 * 내 기업 프로필 시트가 열린다. 완성도가 100% 미만이면 게이지를 보여
 * 채우도록 유도한다.
 */
export function MyProfileChip({ companyName }: MyProfileChipProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data: profile } = useMyCompanyProfile();

  const percent = profile?.completeness.percent;

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="ml-1 flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 py-1 pl-1 pr-2 transition-colors hover:bg-white sm:pr-3"
        aria-label="내 기업 프로필"
      >
        <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full">
          <VendorAvatar
            name={companyName || "공급사"}
            imageUrl={profile?.logoUrl}
            className="h-full w-full text-xs border-0"
          />
        </span>
        <span className="hidden max-w-[140px] truncate text-sm font-medium text-slate-700 lg:block">
          {companyName}
        </span>
        {profile?.trustPercentile != null && (
          <span className="hidden rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-semibold text-primary-text lg:block">
            상위 {profile.trustPercentile}%
          </span>
        )}
        {percent != null && percent < 100 && (
          <span className="hidden items-center gap-1 md:flex">
            <span className="h-1 w-10 overflow-hidden rounded-full bg-slate-200">
              <span
                className="block h-full rounded-full bg-amber-400"
                style={{ width: `${percent}%` }}
              />
            </span>
            <span className="text-2xs tabular-nums text-slate-500">
              {percent}%
            </span>
          </span>
        )}
      </button>
      <MyCompanyProfileSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}
